import React, { Component } from "react";
import { Badge } from "react-bootstrap";
import "./table.css";
import "../../container/reports/report.css";
import { PiPlus, PiFileCsvDuotone } from "react-icons/pi";
import apiError from "../../error";
import axios from "../../axios";
import { toast } from "react-toastify";
import { formatIsoDateForRuleDisplay } from "../../function";

/** Same as report-edit.js — rule attrs whose values are IDs and need lookup for display. */
const ID_RESOLVABLE_RULE_ATTRIBUTES = [
    "department",
    "designation",
    "location",
    "attack-vector",
    "campaign",
];

const emptyRuleLabelMaps = () => ({
    department: {},
    designation: {},
    location: {},
    "attack-vector": {},
    campaign: {},
});

function getNormalizedKeyVariants(header = "") {
    const normalized = String(header || "").trim();
    const camelized = normalized
        .replace(/\s+/g, " ")
        .split(" ")
        .map((part, index) => {
            const cleanPart = part.replace(/[^a-zA-Z0-9]/g, "");
            if (!cleanPart) return "";
            if (index === 0) return cleanPart.charAt(0).toUpperCase() + cleanPart.slice(1);
            return cleanPart.charAt(0).toUpperCase() + cleanPart.slice(1);
        })
        .join("");

    return [
        normalized,
        normalized.toLowerCase(),
        normalized.toUpperCase(),
        normalized.replace(/\s+/g, ""),
        normalized.replace(/\s+/g, "_"),
        normalized.replace(/\s+/g, "-"),
        camelized,
    ].filter(Boolean);
}

function getCellValue(row, header) {
    if (!row || typeof row !== "object") return "---";
    const variants = getNormalizedKeyVariants(header);
    const matchedKey = variants.find((key) => Object.prototype.hasOwnProperty.call(row, key));
    const value = matchedKey ? row[matchedKey] : undefined;
    return value === undefined || value === null || value === "" ? "---" : value;
}

function resolveHeaders(headers, reportList) {
    if (Array.isArray(headers) && headers.length > 0) return headers;
    if (!Array.isArray(reportList) || reportList.length === 0) return [];
    const keys = [];
    const seen = new Set();
    reportList.forEach((row) => {
        if (row && typeof row === "object") {
            Object.keys(row).forEach((k) => {
                if (!seen.has(k)) {
                    seen.add(k);
                    keys.push(k);
                }
            });
        }
    });
    return keys;
}


export default class CustomChatTable extends Component {

    constructor(props) {
        super(props);
        this.state = {
            status: this.props.status,
            customReport: this.props.customReport,
            ruleLabelMaps: emptyRuleLabelMaps(),
        };
    }

    componentDidMount() {
        const cr = this.props.customReport;
        if (cr) this.loadRuleLabelMapsForCustomReport(cr);
    }

    componentDidUpdate(prevProps) {
        const prev = prevProps.customReport;
        const next = this.props.customReport;
        if (
            next &&
            (prev?.id !== next?.id ||
                JSON.stringify(prev?.rule || []) !== JSON.stringify(next?.rule || []))
        ) {
            this.setState({ customReport: next });
            this.loadRuleLabelMapsForCustomReport(next);
        }
    }

    loadRuleLabelMaps = (rules) => {
        const ruleList = Array.isArray(rules) ? rules : [];
        const needDept = ruleList.some((r) => r.attribute === "department");
        const needDesig = ruleList.some((r) => r.attribute === "designation");
        const needLoc = ruleList.some((r) => r.attribute === "location");
        const needAttack = ruleList.some((r) => r.attribute === "attack-vector");
        const needCampaign = ruleList.some((r) => r.attribute === "campaign");
        const empty = emptyRuleLabelMaps();
        if (!needDept && !needDesig && !needLoc && !needAttack && !needCampaign) {
            return Promise.resolve(empty);
        }
        return Promise.all([
            needDept ? axios.get("/api/departments") : Promise.resolve(null),
            needDesig ? axios.get("/api/designations") : Promise.resolve(null),
            needLoc ? axios.get("/api/locations") : Promise.resolve(null),
            needAttack ? axios.get("/api/attacktypes") : Promise.resolve(null),
            needCampaign ? axios.get("/api/campaigns/list") : Promise.resolve(null),
        ])
            .then(([deptRes, desigRes, locRes, attackRes, campRes]) => {
                const maps = emptyRuleLabelMaps();
                (deptRes?.data?.departments || []).forEach((d) => {
                    maps.department[String(d.id)] = d.name;
                });
                (desigRes?.data?.designations || []).forEach((d) => {
                    maps.designation[String(d.id)] = d.name;
                });
                (locRes?.data?.locations || []).forEach((l) => {
                    maps.location[String(l.id)] = l.name;
                });
                const attackTypesList = attackRes?.data?.attackTypes || attackRes?.data?.attackVectors;
                (attackTypesList || []).forEach((a) => {
                    maps["attack-vector"][String(a.id)] = a.name;
                });
                (campRes?.data?.campaigns || []).forEach((c) => {
                    maps.campaign[String(c.id)] = c.name;
                });
                return maps;
            })
            .catch(() => empty);
    };

    loadRuleLabelMapsForCustomReport = (report) => {
        if (!report) return;
        const rules = Array.isArray(report?.rule) ? report.rule : [];
        this.loadRuleLabelMaps(rules).then((ruleLabelMaps) => this.setState({ ruleLabelMaps }));
    };

    formatAttributeLabel = (attribute = "") =>
        String(attribute).replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    getOperatorLabel = (operator = "") => {
        const operatorMap = {
            in: "=",
            eq: "=",
            lt: "<",
            lte: "<=",
            gt: ">",
            gte: ">=",
            between: "Between",
        };
        return operatorMap[operator] || operator;
    };

    getRuleValueText = (rule = {}) => {
        const maps = this.state.ruleLabelMaps || emptyRuleLabelMaps();
        if (Array.isArray(rule.arrayValues) && rule.arrayValues.length) {
            const attr = rule.attribute;
            if (ID_RESOLVABLE_RULE_ATTRIBUTES.includes(attr)) {
                const idToName = maps[attr] || {};
                return rule.arrayValues
                    .map((v) => idToName[String(v)] ?? (formatIsoDateForRuleDisplay(v) || String(v)))
                    .join(", ");
            }
            return rule.arrayValues.map((v) => formatIsoDateForRuleDisplay(v) || String(v)).join(", ");
        }
        if (rule.operator === "between") {
            const min = rule.minRange != null ? formatIsoDateForRuleDisplay(rule.minRange) : "";
            const max = rule.maxRange != null ? formatIsoDateForRuleDisplay(rule.maxRange) : "";
            return `${min} and ${max}`.trim();
        }
        if (rule.maxRange != null && (rule.operator === "lt" || rule.operator === "lte")) {
            return formatIsoDateForRuleDisplay(rule.maxRange);
        }
        if (rule.minRange != null) {
            return formatIsoDateForRuleDisplay(rule.minRange);
        }
        return "";
    };

    renderRuleSummary = (rule, index) => {
        const attributeStyle = { color: "#6016C1", fontWeight: 700 };
        const operatorStyle = { color: "#167F4F", fontWeight: 700 };
        const valueText = this.getRuleValueText(rule);

        return (
            <React.Fragment key={`chat-rule-${index}`}>
                {index > 0 && (
                    <span>
                        {" "}
                        <span style={operatorStyle}>AND</span>{" "}
                    </span>
                )}
                <span style={attributeStyle}>{this.formatAttributeLabel(rule.attribute)}</span>
                <span> </span>
                <span style={operatorStyle}>{this.getOperatorLabel(rule.operator)}</span>
                {valueText ? <span> {valueText}</span> : null}
                <span> </span>
            </React.Fragment>
        );
    };

    renderFilterText = () => {
        const report = this.state.customReport;
        const moduleStyle = { color: "#1967D2", fontWeight: 700 };
        const operatorStyle = { color: "#167F4F", fontWeight: 700 };
        const rules = Array.isArray(report?.rule) ? report.rule : [];

        if (!report) return null;

        return (
            <span className="report-filter-text">
                Showing <span style={moduleStyle}>{this.formatAttributeLabel(report.module || "users")}</span>
                {rules.length ? <span> where </span> : <span> </span>}
                {rules.map((rule, index) => this.renderRuleSummary(rule, index))}
                {report.sortCondition ? (
                    <>
                        <span style={operatorStyle}>Order By</span>{" "}
                        <span>{this.formatAttributeLabel(report.sortCondition)}</span>
                        {report.sortingOrder ? (
                            <span>
                                {" "}
                                (<span>{String(report.sortingOrder).toUpperCase()}</span>)
                            </span>
                        ) : null}
                    </>
                ) : null}
            </span>
        );
    };
    addToReports = async () => {
        try {
            const res = await axios.post('/api/custom-reports/add-to-report', { previewId: this.props.previewId })
            const cr = res.data.customReport;
            this.setState({ customReport: cr }, () => this.loadRuleLabelMapsForCustomReport(cr));
            toast.success(res.data.message);
        } catch (error) {
            apiError(error);
        }
    }
    exportCSV = async () => {
        try {
            const res = await axios.get(`/api/custom-reports/export-csv/${this.props.previewId}`);
            toast.success(res.data.message);
        } catch (error) {
            apiError(error);
        }
    }
    parseEntities(input) {
        const entityMap = {
          "&gt;": "greater than",
          "&gte;": "greater than or equal to",
          "&lt;": "less than",
          "&lte;": "less than or equal to",
          "&eq;": "equal to",
          "&neq;": "not equal to",
          "&and;": "and",
          "&or;": "or",
          "&not;": "not",
          "&plus;": "plus",
          "&minus;": "minus",
          "&mul;": "multiply",
          "&div;": "divide",
          "&mod;": "modulus",
          "&amp;": "and (symbol)",
          "&pipe;": "or (pipe)",
          "&xor;": "exclusive or",
          "&lparen;": "(",
          "&rparen;": ")"
        };
      
        // 🔥 Sort keys by length to avoid partial matching issues
        const pattern = new RegExp(
          Object.keys(entityMap)
            .sort((a, b) => b.length - a.length)
            .join("|"),
          "g"
        );
      
        return input.replace(pattern, (match) => entityMap[match] || match);
    }
    render() {
        const { headers = [], reportList = [], totalCount } = this.props;
        const list = Array.isArray(reportList) ? reportList : [];
        const dynamicHeaders = resolveHeaders(headers, list);
        const count =
            typeof totalCount === "number" && totalCount >= 0 ? totalCount : list.length;
        return (
            <div className="custom-chat-table-wrap">
                <div style={{ overflowX: "auto", width: "100%" }}>
                    {/* <p>{this.parseEntities(this.state.customReport?.name)}</p> */}
                    <div className="report-filter-text-container mb-2">{this.renderFilterText()}</div>
                    <table className="custom-table">
                        <thead>
                            <tr>
                                {dynamicHeaders.length ? (
                                    dynamicHeaders.map((h, idx) => (
                                        <th key={`h-${idx}`}>{h}</th>
                                    ))
                                ) : (
                                    <th>No columns</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {list.length ? (
                                list.map((row, ri) => (
                                    <tr key={row.id != null ? String(row.id) : `r-${ri}`}>
                                        {dynamicHeaders.length ? (
                                            dynamicHeaders.map((h, ci) => {
                                                if (ci === 0) {
                                                    const isUserModule = this.state.customReport?.module === 'user';
                                                    if (isUserModule) {
                                                        const firstName = row.firstName || row.Firstname || row.firstname || row.first_name || '';
                                                        const lastName = row.lastName || row.Lastname || row.lastname || row.last_name || '';
                                                        const fullName = `${firstName} ${lastName}`.trim();
                                                        const email = row.email || row.Email || '';
                                                        return (
                                                            <td key={`c-${ri}-${ci}`}>
                                                                <div>
                                                                    <strong>{fullName || getCellValue(row, h)}</strong>
                                                                    {email ? <div style={{ fontSize: '12px', color: '#888' }}>{email}</div> : null}
                                                                </div>
                                                            </td>
                                                        );
                                                    }
                                                    return <td key={`c-${ri}-${ci}`}>{getCellValue(row, h)}</td>;
                                                }
                                                return <td key={`c-${ri}-${ci}`}>{getCellValue(row, h)}</td>;
                                            })
                                        ) : (
                                            <td>---</td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={Math.max(dynamicHeaders.length, 1)}>No rows</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                </div>
                    <div className="custom-chat-table-footer">
                        {!this.props.isGroupPreview && 
                            <div className="custom-chat-table-footer-buttons">
                                {this.state.customReport.status === "draft" ?
                                    <Badge className="badge-soft-primary" style={{ lineHeight: 1.5, cursor: 'pointer' }} onClick={this.addToReports}>
                                        <PiPlus /> Add to Reports
                                    </Badge> :
                                    <a href={`/reports/report/${this.state.customReport.id}`} target="_blank" rel="noopener noreferrer">
                                        <Badge className="badge-soft-primary" style={{ lineHeight: 1.5, cursor: 'pointer' }}> Go to reports</Badge>
                                    </a>

                                }
                                <Badge className="badge-soft-secondary" style={{ lineHeight: 1.5, cursor: 'pointer' }} onClick={this.exportCSV}>
                                    <PiFileCsvDuotone /> Export CSV
                                </Badge>
                            </div>
                        }
                        <p className="custom-chat-table-footer-text">{count} Records Found</p>
                    </div>

                {
                    list.length > 8 && !this.props.isGroupPreview && (
                        <p className="custom-chat-table-footer-text">This report contains more than 10 records. To view the full results, use <b>Add to Reports</b> or <b>Export CSV</b>.</p>
                    )
                }
            </div>
        );
    }
}
